<template>
  <div v-if="showDialog" class="validation-overlay">
    <div class="validation-dialog">
      <div class="validation-header">
        <h3>⚠️ Budget Progress Validation</h3>
        <p class="validation-subtitle">
          We detected some completion percentages that decreased from the previous draw
        </p>
      </div>
      
      <div class="validation-content">
        <div class="draw-comparison">
          <div class="draw-info">
            <span class="draw-label">Previous Draw:</span>
            <span class="draw-number">Draw {{ validationResult.previous_draw }}</span>
          </div>
          <div class="draw-info">
            <span class="draw-label">Current Draw:</span>
            <span class="draw-number">Draw {{ validationResult.current_draw }}</span>
          </div>
        </div>
        
        <div class="issues-section">
          <h4>Detected Issues:</h4>
          <div class="issues-list">
            <div 
              v-for="issue in validationResult.issues" 
              :key="issue.category"
              class="issue-item"
            >
              <div class="issue-header">
                <span class="category-name">{{ issue.category }}</span>
                <span class="regression-badge">
                  -{{ issue.regression_amount }}%
                </span>
              </div>
              <div class="completion-comparison">
                <div class="completion-bar">
                  <span class="completion-label">Previous:</span>
                  <div class="progress-bar">
                    <div 
                      class="progress-fill previous" 
                      :style="{ width: issue.previous_completion + '%' }"
                    ></div>
                  </div>
                  <span class="completion-text">{{ issue.previous_completion }}%</span>
                </div>
                <div class="completion-bar">
                  <span class="completion-label">Current:</span>
                  <div class="progress-bar">
                    <div 
                      class="progress-fill current" 
                      :style="{ width: issue.current_completion + '%' }"
                    ></div>
                  </div>
                  <span class="completion-text">{{ issue.current_completion }}%</span>
                </div>
              </div>
              <div class="likely-cause">
                <small>{{ issue.likely_cause }}</small>
              </div>
            </div>
          </div>
        </div>
        
        <div class="explanation-section">
          <div class="explanation-box">
            <h4>Why did this happen?</h4>
            <p>
              This usually occurs when "funded at closing" amounts or budget allocations 
              are adjusted in the Excel file, causing the remaining budget calculations 
              to show less work completed than in previous draws.
            </p>
          </div>
        </div>
      </div>
      
      <div class="validation-actions">
        <button 
          @click="handleFixProgression" 
          class="btn btn-primary"
          :disabled="isProcessing"
        >
          {{ isProcessing ? 'Fixing...' : '🔧 Fix Automatically' }}
        </button>
        <button 
          @click="handleAcceptAsIs" 
          class="btn btn-secondary"
          :disabled="isProcessing"
        >
          📊 Accept Excel Values
        </button>
        <button 
          @click="handleManualReview" 
          class="btn btn-outline"
          :disabled="isProcessing"
        >
          ✏️ Manual Review
        </button>
      </div>
      
      <div class="action-explanations">
        <div class="action-explanation">
          <strong>Fix Automatically:</strong> Adjusts completion values to maintain cumulative progress from previous draws
        </div>
        <div class="action-explanation">
          <strong>Accept Excel Values:</strong> Uses the Excel file data as-is (may show negative progress)
        </div>
        <div class="action-explanation">
          <strong>Manual Review:</strong> Allows you to edit individual values manually
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth_store';
import { useErrorStore } from '@/stores/error_store';

// Props
const props = defineProps({
  showDialog: {
    type: Boolean,
    default: false
  },
  validationResult: {
    type: Object,
    default: () => ({})
  },
  templateId: {
    type: [String, Number],
    required: true
  }
});

// Emits
const emit = defineEmits(['close', 'fixed', 'accepted', 'manual-review']);

// Stores
const authStore = useAuthStore();
const errorStore = useErrorStore();

// Reactive data
const isProcessing = ref(false);

// Computed
const authToken = computed(() => authStore.getAuthToken());
const apiBase = computed(() => authStore.getApiBase());

// Methods
async function handleFixProgression() {
  try {
    isProcessing.value = true;
    
    console.log('🔧 VALIDATION: Fixing progression issues for template', props.templateId);
    
    const response = await axios.post(
      `${apiBase.value}api/v1/validation_progress/${props.templateId}/fix`,
      {},
      authToken.value
    );
    
    if (response.data.success) {
      errorStore.setError(
        'Progress Fixed', 
        `Successfully fixed ${response.data.fixed_count} progression issues`, 
        'success', 
        false
      );
      
      emit('fixed', response.data);
      emit('close');
    } else {
      throw new Error(response.data.error || 'Failed to fix progression');
    }
    
  } catch (error) {
    console.error('Error fixing progression:', error);
    errorStore.setError(
      'Fix Error', 
      'Failed to fix progression issues: ' + (error.response?.data?.error || error.message), 
      'error', 
      false
    );
  } finally {
    isProcessing.value = false;
  }
}

function handleAcceptAsIs() {
  console.log('📊 VALIDATION: User accepted Excel values as-is');
  emit('accepted');
  emit('close');
}

function handleManualReview() {
  console.log('✏️ VALIDATION: User chose manual review');
  emit('manual-review');
  emit('close');
}
</script>

<style scoped lang="scss">
.validation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.validation-dialog {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.validation-header {
  text-align: center;
  margin-bottom: 24px;
  
  h3 {
    margin: 0 0 8px 0;
    color: #e67e22;
    font-size: 24px;
  }
  
  .validation-subtitle {
    margin: 0;
    color: #666;
    font-size: 16px;
  }
}

.validation-content {
  margin-bottom: 24px;
}

.draw-comparison {
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  
  .draw-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    
    .draw-label {
      font-size: 14px;
      color: #666;
      margin-bottom: 4px;
    }
    
    .draw-number {
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
    }
  }
}

.issues-section {
  margin-bottom: 24px;
  
  h4 {
    margin: 0 0 16px 0;
    color: #2c3e50;
  }
}

.issues-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.issue-item {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
}

.issue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  .category-name {
    font-weight: 600;
    color: #2c3e50;
  }
  
  .regression-badge {
    background: #e74c3c;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }
}

.completion-comparison {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.completion-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  
  .completion-label {
    min-width: 80px;
    font-size: 14px;
    color: #666;
  }
  
  .progress-bar {
    flex: 1;
    height: 20px;
    background: #e9ecef;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
  }
  
  .progress-fill {
    height: 100%;
    transition: width 0.3s ease;
    
    &.previous {
      background: #28a745;
    }
    
    &.current {
      background: #ffc107;
    }
  }
  
  .completion-text {
    min-width: 50px;
    text-align: right;
    font-weight: 600;
    font-size: 14px;
  }
}

.likely-cause {
  color: #666;
  font-style: italic;
  margin-top: 8px;
}

.explanation-section {
  margin-bottom: 24px;
}

.explanation-box {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 8px;
  padding: 16px;
  
  h4 {
    margin: 0 0 8px 0;
    color: #1976d2;
  }
  
  p {
    margin: 0;
    color: #1565c0;
    line-height: 1.5;
  }
}

.validation-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &.btn-primary {
    background: #28a745;
    color: white;
    
    &:hover:not(:disabled) {
      background: #218838;
    }
  }
  
  &.btn-secondary {
    background: #ffc107;
    color: #212529;
    
    &:hover:not(:disabled) {
      background: #e0a800;
    }
  }
  
  &.btn-outline {
    background: transparent;
    color: #6c757d;
    border: 2px solid #6c757d;
    
    &:hover:not(:disabled) {
      background: #6c757d;
      color: white;
    }
  }
}

.action-explanations {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #dee2e6;
}

.action-explanation {
  font-size: 13px;
  color: #666;
  
  strong {
    color: #2c3e50;
  }
}

@media (max-width: 768px) {
  .validation-dialog {
    padding: 16px;
    margin: 16px;
  }
  
  .draw-comparison {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .validation-actions {
    flex-direction: column;
  }
  
  .completion-bar {
    flex-direction: column;
    gap: 4px;
    
    .completion-label {
      min-width: unset;
    }
  }
}
</style> 